import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './lib/context/AuthContext';
import { SidebarProvider } from './lib/context/SidebarContext';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ToastContainer } from './components/ui/ToastContainer';

// Lazy-loaded pages
const Landing = React.lazy(() => import('./pages/Landing'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const ProfilePage = React.lazy(() => import('./pages/Profile'));
const AgencySettingsPage = React.lazy(() => import('./pages/agency/AgencySettings'));
const AgentManagementPage = React.lazy(() => import('./pages/agency/AgentManagement'));
const NewSalePage = React.lazy(() => import('./pages/sales/NewSale'));
const PasswordResetPage = React.lazy(() => import('./pages/PasswordReset'));
const HelpPage = React.lazy(() => import('./pages/Help'));
const SettingsPage = React.lazy(() => import('./pages/Settings'));
const UnauthorizedPage = React.lazy(() => import('./pages/Unauthorized'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex justify-center items-center h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <SidebarProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/register" element={<AuthPage isRegister={true} />} />
              <Route path="/password-reset" element={<PasswordResetPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* Protected Routes */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route 
                  path="sales/new" 
                  element={
                    <ProtectedRoute requiredRole="agent">
                      <NewSalePage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="agency/settings" 
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AgencySettingsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="agency/agents" 
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AgentManagementPage />
                    </ProtectedRoute>
                  } 
                />
                <Route path="help" element={<HelpPage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <ToastContainer />
        </SidebarProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
